import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Split from 'react-split';
import { Box, useMediaQuery } from '@mui/material';

import { FormIdentifiers } from '../../../config/FormIdentifiers';
import { sxConfig } from './FormBase.style';

const FormBase = ({ name, layoutId, leftPanelContent, rightPanelContent, verticalLayout = false }) => {
  const isMediumScreen = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const [sizes, setSizes] = useState([30, 70]);
  const [topPanelHeight, setTopPanelHeight] = useState(null);

  const containerRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const savedSizes = localStorage.getItem('split-sizes');
    if (savedSizes) {
      setSizes(JSON.parse(savedSizes));
    }
  }, []);

  useEffect(() => {
    const settings = JSON.parse(localStorage.getItem('form-layout-settings') || '{}');

    setTopPanelHeight(settings?.[name]?.[layoutId]?.verticalHeight ?? null);
  }, [name, layoutId]);

  const handleDragEnd = (newSizes) => {
    setSizes(newSizes);
    localStorage.setItem('split-sizes', JSON.stringify(newSizes));
  };

  const handleVerticalDragStart = (e) => {
    if (e.button !== 0) return;

    e.preventDefault();

    const container = containerRef.current;
    if (!container) return;

    const containerHeight = container.getBoundingClientRect().height;

    dragRef.current = {
      startY: e.clientY,
      startHeight: topPanelHeight ?? containerHeight,
    };

    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleVerticalDrag = (e) => {
    if (!dragRef.current) return;

    const { startY, startHeight } = dragRef.current;
    const deltaY = e.clientY - startY;

    const maxHeight = containerRef.current?.clientHeight ?? startHeight;

    setTopPanelHeight(Math.min(maxHeight, Math.max(100, startHeight + deltaY)));
  };

  const handleVerticalDragEnd = (e) => {
    if (!dragRef.current) return;

    dragRef.current = null;

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    if (layoutId == null || topPanelHeight == null) return;

    const settings = JSON.parse(localStorage.getItem('form-layout-settings') || '{}');

    localStorage.setItem(
      'form-layout-settings',
      JSON.stringify({
        ...settings,
        [name]: {
          ...settings[name],
          [layoutId]: {
            ...settings[name]?.[layoutId],
            verticalHeight: topPanelHeight,
          },
        },
      })
    );
  };

  if (!name) {
    console.error('name is required but was not provided.');
    return null; // Prevent the component from rendering if the identifier is not provided
  }

  return (
    <Box
      sx={{
        ...sxConfig.containerBox,
        ...(verticalLayout && {
          position: 'relative',
          zIndex: 0,
        }),
      }}
    >
      {isMediumScreen && !verticalLayout ? (
        <Split
          sizes={sizes}
          minSize={0}
          gutterSize={5}
          direction="horizontal"
          style={{ display: 'flex', height: '100%', flexDirection: 'row' }}
          onDragEnd={handleDragEnd}
        >
          <Box sx={sxConfig.formBox}>{leftPanelContent}</Box>
          <Box sx={sxConfig.formBox}>{rightPanelContent}</Box>
        </Split>
      ) : (
        <Box
          ref={containerRef}
          sx={{
            height: '100%',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              ...sxConfig.formBox,
              height: topPanelHeight ?? '100%',
              minHeight: 100,
              flexShrink: 0,
              overflow: 'auto',
            }}
          >
            {leftPanelContent}
          </Box>

          {isMediumScreen && (
            <Box
              className="gutter gutter-vertical"
              onPointerDown={handleVerticalDragStart}
              onPointerMove={handleVerticalDrag}
              onPointerUp={handleVerticalDragEnd}
              onPointerCancel={handleVerticalDragEnd}
              sx={{
                height: 5,
                flexShrink: 0,
                touchAction: 'none',
                userSelect: 'none',
              }}
            />
          )}

          <Box
            sx={{
              ...sxConfig.formBox,
              height: '100%',
              minHeight: 0,
              flexShrink: 0,
              mt: isMediumScreen ? 0 : 2,
            }}
          >
            {rightPanelContent}
          </Box>
        </Box>
      )}
    </Box>
  );
};
FormBase.propTypes = {
  name: PropTypes.oneOf(Object.values(FormIdentifiers)).isRequired,
  layoutId: PropTypes.number,
  leftPanelContent: PropTypes.node.isRequired,
  rightPanelContent: PropTypes.node.isRequired,
  verticalLayout: PropTypes.bool,
};

export default FormBase;
